import './MissionsSectionStyles.scss';
import { useMutation, useQuery } from '@apollo/client';
import {
  Container,
  EMPTY_LIST,
  LoadingOrError,
  comparing,
  useParamHandler,
  useScreenSize,
} from '@custom/dx3';
import { Tab, Tabs } from '@mui/material';
import { type SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { COPY } from '../../assets/copy/copy';
import featuredMission from '../../assets/images/featuredMission.png';
import featuredMissionMobile from '../../assets/images/featuredMissionMobile.jpg';
import {
  type FeaturedMission,
  FeaturedMissions,
} from '../../components/FeaturedMissions/FeaturedMissions';
import { MissionModal } from '../../containers/MissionModal/MissionModal';
import { MissionFlag } from '../../models/__generated__/graphql';
import ROUTES from '../../routes/routes';
import { SET_MISSION_LIKE } from '../../store/mutations/mutations';
import {
  GET_DASHBOARD_DATA,
  GET_FEATURED_MISSION_DATA,
  GET_MISSIONS_SECTION_DATA,
} from '../../store/queries/containerQueries';
import { useDelayedQuery } from '../../utils/customHooks/useDelayedQuery';
import { useMissionsFilter } from '../../utils/customHooks/useMissionsFilter';
import { ISODateToDate } from '../../utils/formatters';
import { logError } from '../../utils/logError.js';
import { MISSION_ID_PARAM } from '../../utils/routingHelpers.js';
import { MissionActions } from './MissionActions/MissionActions';
import { MissionsDisplay } from './MissionsDisplay/MissionsDisplay';

enum TAB_OPTIONS {
  FEATURED,
  ALL,
  LIKED,
  COMPLETED,
}

export function MissionsSection(): JSX.Element {
  const { category, setCategory, filterVariables } = useMissionsFilter();

  const [activeTab, setActiveTab] = useState<TAB_OPTIONS>(TAB_OPTIONS.ALL);

  const navigate = useNavigate();

  const navigateHome = (searchString?: string) => {
    navigate(
      {
        pathname: ROUTES.HOME,
        search: searchString,
      },
      {
        replace: true,
      }
    );
  };

  const isMobile = useScreenSize() === 'mobile';

  const handleChange = (_: SyntheticEvent, newValue: TAB_OPTIONS) => {
    switch (newValue) {
      case TAB_OPTIONS.ALL:
        navigateHome(createSearchParams().toString());
        break;
      case TAB_OPTIONS.LIKED:
        navigateHome(
          createSearchParams({
            flag: MissionFlag.Eligible,
            liked: 'true',
          }).toString()
        );
        break;
      case TAB_OPTIONS.FEATURED:
        navigateHome(
          createSearchParams({
            flag: MissionFlag.Featured,
          }).toString()
        );
        break;
      case TAB_OPTIONS.COMPLETED:
        navigateHome(
          createSearchParams({
            flag: MissionFlag.Completed,
          }).toString()
        );
        break;
    }

    setActiveTab(newValue);
  };

  const { data, loading, error, refetch } = useQuery(
    GET_MISSIONS_SECTION_DATA,
    {
      fetchPolicy: 'cache-and-network',
      variables: filterVariables,
    }
  );

  const featuredMissionQuery = useQuery(GET_FEATURED_MISSION_DATA, {
    fetchPolicy: 'cache-and-network',
  });

  const defaultFeaturedMission = useMemo(
    (): FeaturedMission[] => [
      {
        id: null,
        imageUrl: isMobile ? featuredMissionMobile : featuredMission,
        name: COPY.FEATURED_MISSION.NO_MISSIONS_FOUND.HEADER,
        description: COPY.FEATURED_MISSION.NO_MISSIONS_FOUND.DESCRIPTION,
        locked: false,
        point: null,
        alternativeButtonText:
          COPY.FEATURED_MISSION.NO_MISSIONS_FOUND.BUTTON_TEXT,
        textColor: '#000',
        expiringDate: null,
        inProgress: false,
        repeats: false,
        expiringSoon: false,
      },
    ],
    [isMobile]
  );

  const displayableFeaturedMissions: FeaturedMission[] = useMemo(() => {
    if (featuredMissionQuery.loading || !featuredMissionQuery.data) {
      return [];
    }

    const missionData = featuredMissionQuery.data;

    if (!missionData?.me?.missions?.length) {
      return defaultFeaturedMission;
    }

    return missionData?.me?.missions
      ?.filter((mission) => mission.largeTileImageUrl && mission.name)
      .sort(
        comparing(
          (mission) =>
            -1 *
            (mission?.startDate
              ? ISODateToDate(mission.startDate)?.getTime() ?? 0
              : 0)
        )
      )
      .map((mission): FeaturedMission => {
        return {
          ...mission,
          expiringDate: ISODateToDate(mission.expiringDate),
          id: mission.id,
          imageUrl: isMobile
            ? mission.iconImageUrl ?? mission.largeTileImageUrl
            : mission.largeTileImageUrl,
          name: mission.name,
          point: null,
          description: mission.description ?? '',
          textColor: mission.textColor,
          locked: mission.lockedStatus?.locked ?? false,
        };
      });
  }, [featuredMissionQuery, defaultFeaturedMission, isMobile]);

  const [setMissionLike] = useMutation(SET_MISSION_LIKE);

  const [selectedMissionId, setSelectedMissionId] = useParamHandler(
    MISSION_ID_PARAM,
    (str) => {
      navigate(str, { replace: true });
    }
  );

  const handleClose = useCallback(() => {
    refetch().catch(logError);
    setSelectedMissionId(null);
  }, [setSelectedMissionId, refetch]);

  const handleMissionLiking = useCallback(
    async (id: string, like: boolean) => {
      await setMissionLike({
        variables: {
          iLike: like,
          missionId: id,
        },
      });
    },
    [setMissionLike]
  );

  useDelayedQuery(GET_DASHBOARD_DATA, {}, !!data);

  const sortedMissions = useMemo(() => {
    if (!data?.me?.missions) {
      return EMPTY_LIST;
    }

    return data.me.missions.toSorted(
      comparing((it) => {
        return (ISODateToDate(it.startDate)?.getTime() || 0) * -1;
      })
    );
  }, [data]);

  return (
    <Container
      backingProps={{
        className: 'missions-screen-container',
      }}
    >
      <div className="section-header">
        <h2 className="bold">{COPY.MISSIONS.HEADER}</h2>
        <div className="decorator" />
      </div>
      <Container>
        <LoadingOrError
          loading={featuredMissionQuery.loading}
          error={featuredMissionQuery.error}
        >
          <FeaturedMissions missions={displayableFeaturedMissions} />
        </LoadingOrError>
      </Container>
      <Tabs
        value={activeTab}
        onChange={handleChange}
        aria-label="filter liked missions"
        indicatorColor="secondary"
        className={'tab-container'}
        variant="scrollable"
      >
        <Tab label="All Missions" value={TAB_OPTIONS.ALL} />
        <Tab label="Featured" value={TAB_OPTIONS.FEATURED} />
        <Tab label="Favorited" value={TAB_OPTIONS.LIKED} />
        <Tab label="Completed" value={TAB_OPTIONS.COMPLETED} />
      </Tabs>

      <MissionActions category={category} setCategory={setCategory} />
      <LoadingOrError loading={!data && loading} error={error}>
        <div className="missions-section">
          <MissionsDisplay
            missions={sortedMissions}
            setLike={handleMissionLiking}
          />
        </div>
      </LoadingOrError>
      <MissionModal missionId={selectedMissionId} handleClose={handleClose} />
    </Container>
  );
}
